import { PublicClientApplication, AccountInfo, AuthenticationResult } from "@azure/msal-browser";

const getMFAValues = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('platform.partsol.ai')) {
        return {
            'authority': 'https://partsolb2c.b2clogin.com/partsolb2c.onmicrosoft.com/B2C_1_signin_tenants',
            'redirectUri': 'https://platform.partsol.ai/auth/callback',
            'clientId': "2d154b14-1d4e-4ef2-8ea6-fc186760c48a"
        }
    }
    else {
        return {
            'authority': process.env.REACT_APP_AUTHORITY || '',
            'redirectUri': process.env.REACT_APP_AZURE_REDIRECT || '',
            'clientId': process.env.REACT_APP_AZURE_CLIENT_ID || ''
        }
    }
}

const mfaValues = getMFAValues();

const msalConfig = {
    auth: {
        clientId: mfaValues.clientId,
        authority: mfaValues.authority,
        knownAuthorities: ['partsolb2c.b2clogin.com'],
        redirectUri: mfaValues.redirectUri,
        navigateToLoginRequestUrl: false,
    },
    cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false
    },
};

// Singleton MSAL instance
let msalInstance: PublicClientApplication | null = null;
let initializationPromise: Promise<void> | null = null;
let redirectPromiseHandled = false;

/**
 * Get or create the shared MSAL instance
 */
export const getMsalInstance = async (): Promise<PublicClientApplication> => {
    if (!msalInstance) {
        msalInstance = new PublicClientApplication(msalConfig);
    }
    
    if (!initializationPromise) {
        initializationPromise = msalInstance.initialize();
    }
    
    await initializationPromise;
    return msalInstance;
};

/**
 * Handle redirect promise - ensures it's only called once
 * Returns the authentication result if available
 */
export const handleRedirectPromise = async (): Promise<AuthenticationResult | null> => {
    // Prevent multiple simultaneous calls
    if (redirectPromiseHandled) {
        return null;
    }
    
    try {
        const instance = await getMsalInstance();
        const response = await instance.handleRedirectPromise();
        redirectPromiseHandled = true;
        
        // Clear hash from URL after processing to prevent issues
        if (response && window.location.hash) {
            // Use replaceState to remove hash without triggering navigation
            window.history.replaceState(
                null,
                '',
                window.location.pathname + window.location.search
            );
        }
        
        return response;
    } catch (error) {
        redirectPromiseHandled = true;
        throw error;
    }
};

/**
 * Reset redirect promise handling flag - call this when starting a new login flow
 */
export const resetRedirectPromise = () => {
    redirectPromiseHandled = false;
};

/**
 * Clear all MSAL cache and accounts
 * Should be called on logout
 * Note: This clears local cache only, doesn't logout from Azure AD (to avoid redirects)
 */
export const clearMsalCache = async (): Promise<void> => {
    try {
        const instance = await getMsalInstance();
        
        // Clear the cache (this removes all accounts and tokens from sessionStorage)
        await instance.clearCache();
        
        // Reset redirect promise flag
        resetRedirectPromise();
        
        // Clear any remaining hash from URL
        if (window.location.hash) {
            window.history.replaceState(
                null,
                '',
                window.location.pathname + window.location.search
            );
        }
    } catch (error) {
        console.error('Error clearing MSAL cache:', error);
        // Even if cache clear fails, manually clear sessionStorage
        try {
            // Clear MSAL-related sessionStorage items
            Object.keys(sessionStorage).forEach(key => {
                if (key.startsWith('msal.')) {
                    sessionStorage.removeItem(key);
                }
            });
            // Reset redirect promise flag
            resetRedirectPromise();
        } catch (storageError) {
            console.error('Error clearing MSAL storage:', storageError);
        }
    }
};

/**
 * Get the MSAL config (for components that need it)
 */
export const getMsalConfig = () => msalConfig;

/**
 * Check if we're on the callback route
 */
export const isCallbackRoute = (): boolean => {
    return window.location.pathname === '/auth/callback';
};

